
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useFormContext } from 'react-hook-form';

interface SiteAddressFieldsProps {
  form: UseFormReturn<any>;
  showToggle?: boolean;
}

const SiteAddressFields: React.FC<SiteAddressFieldsProps> = ({ form, showToggle = true }) => {
  // Add defensive check for form context
  const formContext = useFormContext();
  
  // If no form context is available, don't render anything to prevent crashes
  if (!formContext) {
    console.warn('SiteAddressFields: No form context available');
    return null;
  }

  const useSeparateSiteAddress = form.watch('use_separate_site_address');

  return (
    <div className="space-y-4">
      {showToggle && (
        <FormField
          control={form.control}
          name="use_separate_site_address"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Different Site Address
                </FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable if the project site address is different from customer address
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {useSeparateSiteAddress && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/50">
          <div className="md:col-span-2">
            <h4 className="text-sm font-medium mb-3">Site Address</h4>
          </div>
          
          <FormField
            control={form.control}
            name="site_street_address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street Address</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter site street address" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="site_suburb"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Suburb</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter site suburb" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="site_city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter site city" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="site_province_state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Province/State</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter site province/state" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="site_postal_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal Code</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter site postal code" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SiteAddressFields;
